import React, { FC, ReactNode, useEffect, useRef } from 'react';
import qs from 'query-string';
import { useNavigate } from 'react-router-dom';
import { SEARCH_KEYS } from '../../constants/query-params/search';
import cn from '../../utils/classnames';
import DropdownButton, { DropdownButtonProps } from './components/dropdown-button/dropdown-button';
import DropdownItem from './components/dropdown-item/dropdown-item';
import DropdownContext from './contexts/dropdown.context';
import { DropDirectionType, MenuAlignType } from './hooks/use-dropdown-position';
import useDropdownState from './hooks/use-dropdown-state';
import './dropdown.scss';

export type DropdownOption = {
    id?: number | string;
    title: string;
    active?: boolean;
    disabled?: boolean;
    child?: JSX.Element;
    'data-testid'?: string;
};

export type DropdownProps = Omit<DropdownButtonProps, 'onSelect' | 'title'> & {
    className?: string;
    id?: string;
    disabled?: boolean;
    label?: string;
    onSelect?: (arg: DropdownOption) => void;
    options: DropdownOption[];
    onBeforeHistoryPush?: (arg?: DropdownOption) => void;
    selectedValue: DropdownOption;
    menuAlign?: MenuAlignType;
    dropDirection?: DropDirectionType;
    withQuery?: boolean;
    icon?: ReactNode;
};

const Dropdown: FC<DropdownProps> = (props) => {
    const {
        label,
        className,
        selectedValue,
        options,
        onSelect,
        onBeforeHistoryPush,
        withQuery = false,
        disabled,
        autoClose,
        dropDirection,
        menuAlign,
        id = 'dropdown-native',
        icon
    } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownState = useDropdownState({ menuAlign, autoClose, dropDirection });
    const { isOpen, setIsOpen, menuRef, styles, isCalculating } = dropdownState;

    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen((prev) => !prev);
        }
    };

    const handleOptionClick = (option: DropdownOption) => {
        if (option.disabled) {
            return;
        }

        setIsOpen(false);

        if (!withQuery) {
            onSelect?.(option);
        } else {
            const search = qs.parse(window.location.search, { arrayFormat: 'comma' });

            if (search[SEARCH_KEYS.SORTING] === option.id) {
                return;
            }

            const url = `${window.location.pathname}?${qs.stringify(
                {
                    ...search,
                    query: search.query || '',
                    [SEARCH_KEYS.SORTING]: option.id
                },
                { arrayFormat: 'comma' }
            )}`;

            onBeforeHistoryPush?.(option);
            navigate(url);
        }
    };

    return (
        <DropdownContext.Provider value={dropdownState}>
            <div className={cn('lmp-dropdown-container', className)} ref={containerRef}>
                {label && <span className='dropdown-label'>{label}</span>}

                <div className={cn('custom-dropdown dropdown ms-12', { 'is-disabled': disabled })}>
                    <button
                        type='button'
                        id={id}
                        className='dropdown-toggle'
                        onClick={toggleDropdown}
                        disabled={disabled}
                    >
                        {selectedValue.child ? (
                            <>{selectedValue.child}</>
                        ) : (
                            <span className='dropdown-title'>{selectedValue.title}</span>
                        )}
                        {icon ? (
                            <span className={cn('arrow-icon-wrapper', { 'is-open': isOpen })}>{icon}</span>
                        ) : (
                            <i className={cn('arrow-icon', { 'is-open': isOpen })} />
                        )}
                    </button>
                    {isOpen && (
                        <ul
                            ref={menuRef}
                            className='dropdown-menu'
                            role='listbox'
                            style={{
                                ...styles,
                                visibility: isCalculating ? 'hidden' : 'visible',
                                opacity: isCalculating ? 0 : 1
                            }}
                        >
                            {options.map((option) => (
                                <DropdownItem
                                    key={option.id}
                                    className={cn({ 'is-active': option.active || selectedValue.id === option.id })}
                                    option={option}
                                    disabled={option.disabled}
                                    onClick={() => handleOptionClick(option)}
                                    selected={selectedValue.id === option.id}
                                >
                                    {(option?.child || option.title) as string}
                                </DropdownItem>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </DropdownContext.Provider>
    );
};

export default Object.assign(Dropdown, {
    Button: DropdownButton,
    Item: DropdownItem
});
